import { RuleId } from '@wix/a11y-rules';
import { ITEM_TYPES } from '@wix/advanced-seo-utils';
import { useTranslation } from '@wix/fe-essentials/react';
import { convertElementStatusToBooleanStatus, FixerType, RuleListStatus, ToggleType, useCollapseItems } from '@wix/rules-panel-ui';
import { isOptional } from '@wix/seo-common-core';
import { useAtom, useSetAtom } from 'jotai';
import React, { useCallback } from 'react';
import { currentTaskInfoAtom } from '../../atoms/bi-atoms';
import { altTextScreenPageIdAtom } from '../../atoms/session-atoms';
import { BiCtaNames } from '../../bi/types';
import { AltTextNoImagesGreenState } from '../../components/fixers/alt-image-fixer/alt-text-no-images-green-state';
import AltTextImageGrid from '../../components/fixers/alt-image-fixer/drill-in/alt-text-image-grid/alt-text-image-grid';
import { RuleDescriptionComponent } from '../../components/rule-description-component';
import { usePanelStateAtom } from '../../hooks/rules-state';
import { useA11yWizardAdapter } from '../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../hooks/use-bi-logger';
import { useNavigation } from '../../hooks/use-navigation';
import { useOpenArticleByEditorHost } from '../../hooks/use-open-article-by-editor-host';
import { getUpdatedAltTextImagesFromState } from '../rule-handlers/alt-text/get-updated-alt-text-images-from-state';
import { useBaseAltTextRuleHandlers } from '../rule-handlers/alt-text-v2/use-base-alt-text-rule-handlers';
import { getPageTypeForBi } from '../shared/bi-helpers';
import { isStaticPage, shouldUpdateRuleStatus } from '../shared/helpers';
export const useAltTextRule = () => {
  const {
    t
  } = useTranslation();
  const {
    getRuleId,
    getIsNotApplicable,
    getRuleStatus,
    extractAltImageProps,
    getGreenStateDescriptionKey
  } = useBaseAltTextRuleHandlers();
  const {
    editorHost
  } = useA11yWizardAdapter();
  const navigateToPage = useNavigation();
  const {
    openSelectedArticle
  } = useOpenArticleByEditorHost();
  const biLogger = useBiLogger();
  const [currentTaskInfo, setCurrentTaskInfo] = useAtom(currentTaskInfoAtom);
  const {
    getRuleState,
    getGroupData,
    panelState,
    setRuleState
  } = usePanelStateAtom();
  const setAltTextScreenProps = useSetAtom(altTextScreenPageIdAtom);
  const {
    collapseRule
  } = useCollapseItems();
  const getAltTextRule = useCallback(_ref => {
    var _ruleState$state;
    let {
      ruleExecutionResult,
      pageId,
      routerId,
      isCrossPage
    } = _ref;
    if (isOptional(pageId)) {
      throw new Error('pageId is required');
    }
    const ruleId = getRuleId(ruleExecutionResult);
    const ruleState = getRuleState(pageId, RuleId.STATIC_ALT_TEXT);
    const {
      analysisState,
      itemType
    } = getGroupData(pageId ?? '') ?? {};
    if (isOptional(itemType)) {
      throw new Error('itemType is required');
    }
    const isNotApplicable = getIsNotApplicable(ruleExecutionResult);
    if (isNotApplicable && isStaticPage(itemType)) {
      return null;
    }
    const uiStatus = getRuleStatus(ruleExecutionResult, pageId);
    const baseAltImageProps = extractAltImageProps(ruleExecutionResult, pageId);
    const altImageProps = getUpdatedAltTextImagesFromState(baseAltImageProps, ruleState == null || (_ruleState$state = ruleState.state) == null ? void 0 : _ruleState$state.savedImages, panelState.globalSectionComponentsDataMap);
    const elementCount = altImageProps.length;
    const failedElementCount = altImageProps.filter(rule => !convertElementStatusToBooleanStatus(rule.status)).length;
    if (shouldUpdateRuleStatus(analysisState, uiStatus, ruleState, routerId)) {
      setRuleState(pageId, RuleId.STATIC_ALT_TEXT, {
        status: uiStatus
      });
    }
    return {
      description: /*#__PURE__*/React.createElement(RuleDescriptionComponent, {
        i18nKey: uiStatus === RuleListStatus.passed ? 'accessibility-wizard.rules-list.static-alt-text.description' : 'accessibility-wizard.rules-list.static-alt-text.description.failed',
        openArticle: () => {
          openSelectedArticle(editorHost, 'editor-package-accessibility-wizard.articles-alt-text-help');
          biLogger.taskCtaClick({
            ctaName: BiCtaNames.LearnMore,
            elementCount,
            failedElementCount
          });
        },
        title: uiStatus === RuleListStatus.passed ? t('accessibility-wizard.rules-list.issue-resolved') : ''
      }),
      elementCount,
      failedElementCount,
      fixer: FixerType.Custom,
      fixerProps: {
        customFixer: isNotApplicable ? /*#__PURE__*/React.createElement(AltTextNoImagesGreenState, {
          description: t(getGreenStateDescriptionKey(itemType))
        }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(AltTextImageGrid, {
          images: altImageProps,
          openDrillInScreen: () => {
            setAltTextScreenProps({
              itemType,
              pageId
            });
          },
          taskStatus: uiStatus !== RuleListStatus.failed,
          onClickCancel: collapseRule
        }), itemType === ITEM_TYPES.RESTAURANTS_MENU_PAGE && /*#__PURE__*/React.createElement(AltTextNoImagesGreenState, {
          description: t(getGreenStateDescriptionKey(itemType))
        }))
      },
      id: `${routerId ?? pageId}-${ruleId}`,
      label: t('accessibility-wizard.rules-list.static-alt-text.title'),
      onRuleToggle: function (toggleType, ruleIndex, initiatedByUser) {
        if (initiatedByUser === void 0) {
          initiatedByUser = false;
        }
        setCurrentTaskInfo(taskInfo => ({
          ...taskInfo,
          status: uiStatus,
          task_index: ruleIndex,
          task_name: ruleId
        }));
        biLogger.taskExpandCollapse({
          elementCount,
          failedElementCount,
          initiatedByUser,
          taskInfo: {
            ...currentTaskInfo,
            page_type: getPageTypeForBi(itemType, isCrossPage),
            status: uiStatus,
            task_index: ruleIndex,
            task_name: ruleId
          },
          toggleType
        });
        if (toggleType === ToggleType.Expand) {
          navigateToPage(pageId);
        }
      },
      onStatusChange: () => {
        setCurrentTaskInfo(taskInfo => ({
          ...taskInfo,
          status: uiStatus
        }));
        biLogger.taskStatusChange({
          elementCount,
          failedElementCount,
          status: uiStatus
        });
      },
      parentGroupId: pageId,
      ruleId,
      status: uiStatus
    };
  }, [getRuleId, getRuleState, getGroupData, getIsNotApplicable, getRuleStatus, extractAltImageProps, panelState.globalSectionComponentsDataMap, t, collapseRule, setRuleState, openSelectedArticle, editorHost, biLogger, setAltTextScreenProps, setCurrentTaskInfo, currentTaskInfo, navigateToPage, getGreenStateDescriptionKey]);
  return {
    getAltTextRule
  };
};
//# sourceMappingURL=use-alt-text-rule.js.map